import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';

const API_BASE = (process.env.REACT_APP_API_BASE || 'https://backend.parthmerchant.com').replace(/\/$/, '');

const MODE_KEY = 'pm-admin-mode';

// The palette catalogue, with the swatches the Appearance tab renders. The
// actual colour values live in src/themes.css (itself a mirror of the site's
// App.css) — these four hexes per theme are just the preview chips, same as the
// site's own theme picker.
export const LIGHT_THEMES = [
  { id: 'light-classic', label: 'Light Classic', bg: '#f4efe2', palette: ['#c47aab', '#1c2c5e', '#3a9970', '#8b5e3c'] },
  { id: 'paper-beige',   label: 'Paper Beige',   bg: '#efe9da', palette: ['#b06a4f', '#7a8b6f', '#4a4a44', '#9a8d6f'] },
  { id: 'snow',          label: 'Snow',          bg: '#f7f8fa', palette: ['#5b7a99', '#3f5e7d', '#8a929e', '#d4d9e0'] },
];

export const DARK_THEMES = [
  { id: 'space-native', label: 'Space Native', bg: '#1a1624', palette: ['#c47aab', '#8b2e50', '#6d5a9c', '#d4cee0'] },
  { id: 'gruvbox',      label: 'Gruvbox',      bg: '#282828', palette: ['#b8bb26', '#fe8019', '#fabd2f', '#83a598'] },
  { id: 'graphite',     label: 'Graphite',     bg: '#1c1e21', palette: ['#7e8a99', '#9aa6b3', '#b7c0cc', '#dfe4ea'] },
  { id: 'mono',         label: 'Mono',         bg: '#0a0a0a', palette: ['#ffffff', '#bdbdbd', '#7a7a7a', '#444444'] },
  { id: 'slate',        label: 'Slate',        bg: '#1b2027', palette: ['#7e9cb8', '#9bb0c4', '#a4adb8', '#404a57'] },
  { id: 'ash',          label: 'Ash',          bg: '#201e1c', palette: ['#a8957f', '#9a8f80', '#b3aaa0', '#4a443d'] },
  { id: 'cinder',       label: 'Cinder',       bg: '#141210', palette: ['#cdc2b4', '#a8a097', '#b0a89e', '#423e39'] },
];

export const DEFAULT_SITE_THEME = {
  lightTheme: 'paper-beige',
  darkTheme: 'cinder',
  defaultMode: 'light',
};

const ThemeContext = createContext(null);

function readMode() {
  try {
    const saved = localStorage.getItem(MODE_KEY);
    return saved === 'light' || saved === 'dark' ? saved : null;
  } catch {
    return null;
  }
}

/**
 * The console renders in whichever light/dark palette pair the *site* is
 * configured with, so the Appearance tab is a live preview of what visitors
 * get rather than a form you have to imagine the result of. Which half of the
 * pair you're looking at is a per-device choice; it defaults to the site's own
 * `defaultMode` until you flip it.
 */
export function ThemeProvider({ children }) {
  const [siteTheme, setSiteTheme] = useState(DEFAULT_SITE_THEME);
  const [mode, setModeState] = useState(readMode);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch(`${API_BASE}/api/landing/content`);
        if (!res.ok) throw new Error(`${res.status}`);
        const data = await res.json();
        if (!cancelled && data?.theme) setSiteTheme({ ...DEFAULT_SITE_THEME, ...data.theme });
      } catch {
        // Keep the defaults — a console that can't reach the API must still be
        // legible enough to sign in and look at the error.
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const effectiveMode = mode || siteTheme.defaultMode;
  const isDark = effectiveMode === 'dark';
  const themeId = isDark ? siteTheme.darkTheme : siteTheme.lightTheme;

  useEffect(() => {
    document.documentElement.setAttribute('data-theme', themeId);
  }, [themeId]);

  const setMode = useCallback((next) => {
    setModeState(next);
    try { localStorage.setItem(MODE_KEY, next); } catch { /* noop */ }
  }, []);

  const toggle = useCallback(() => setMode(isDark ? 'light' : 'dark'), [isDark, setMode]);

  const value = useMemo(() => ({
    siteTheme, setSiteTheme, themeId, mode: effectiveMode, isDark, setMode, toggle,
  }), [siteTheme, themeId, effectiveMode, isDark, setMode, toggle]);

  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
}

export function useTheme() {
  const ctx = useContext(ThemeContext);
  if (!ctx) throw new Error('useTheme must be used within ThemeProvider');
  return ctx;
}
